import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { usersApi } from './users.api';
import { userKeys } from './users.queries';
import { handoverScanningKeys } from '@/features/handover-scanning';
import type { AssignRoleRequest } from '@/features/roles';
import type {
  UsersListParams,
  CreateUserRequest,
  UpdateUserRequest,
  DriverAvailability,
  OperatorAvailability,
  BulkImportResult
} from '../types';

export function useCreateUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    }
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      usersApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    }
  });
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (id: number) => usersApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useAssignRoleToUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: ({
      userId,
      data
    }: {
      userId: number;
      data: AssignRoleRequest;
    }) => usersApi.assignRole(userId, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('assignRoleSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    }
  });
}

export function useRemoveRoleFromUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: ({
      userId,
      data
    }: {
      userId: number;
      data: AssignRoleRequest;
    }) => usersApi.removeRole(userId, data),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('removeRoleSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    }
  });
}

export function useDeactivateUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (userId: number) => usersApi.deactivate(userId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('deactivateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useUnlockUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (userId: number) => usersApi.unlock(userId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('unlockSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useActivateUserMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (userId: number) => usersApi.activate(userId),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('activateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useBulkDeleteUsersMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<UsersListParams> }
    ) => usersApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useBulkActivateUsersMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<UsersListParams> }
    ) => usersApi.bulkActivate(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkActivateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useBulkDeactivateUsersMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<UsersListParams> }
    ) => usersApi.bulkDeactivate(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkDeactivateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useUpdateDriverAvailabilityMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: ({
      userId,
      status
    }: {
      userId: number;
      status: DriverAvailability;
    }) => usersApi.updateDriverAvailability(userId, status),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('driverAvailabilityUpdated')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    }
  });
}

export function useUpdateOperatorAvailabilityMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation({
    mutationFn: ({
      userId,
      status
    }: {
      userId: number;
      status: OperatorAvailability;
    }) => usersApi.updateOperatorAvailability(userId, status),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('operatorAvailabilityUpdated')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      queryClient.invalidateQueries({
        queryKey: handoverScanningKeys.metrics()
      });
    }
  });
}

export function useImportUsersMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('users');

  return useMutation<
    BulkImportResult,
    Error,
    { file: File; onProgress?: (n: number) => void }
  >({
    mutationFn: ({ file, onProgress }) => usersApi.bulkImport(file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
    meta: {
      operationType: 'create',
      skipGlobalErrorToast: true,
      fallbackSuccessMessage: t('importSuccess')
    }
  });
}
